import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { Loader2, Send, Shield } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function Messages() {
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const { user, isFirearmsPassActive, isAgeVerified } = useAuth();
  const navigate = useNavigate();

  const loadConvs = async () => {
    const { data } = await api.get("/conversations");
    setConvs(data);
    setLoading(false);
    if (data.length && !active) setActive(data[0]);
  };

  useEffect(() => { loadConvs(); }, []);

  useEffect(() => {
    if (!active) return;
    api.get(`/conversations/${active.conversation_id}/messages`).then(({ data }) => setMsgs(data));
  }, [active]);

  const otherId = (c) => c.participants.find((p) => p !== user?.user_id);

  const activeIsFirearms = !!active?.is_firearms;
  const needsPass = activeIsFirearms && !isFirearmsPassActive;
  const needsVerify = activeIsFirearms && isFirearmsPassActive && !isAgeVerified;
  const gated = needsPass || needsVerify;

  const send = async () => {
    if (!active || !body.trim()) return;
    if (gated) {
      if (needsPass) return navigate("/subscribe");
      return navigate("/firearms/verify?next=/messages");
    }
    setSending(true);
    try {
      await api.post("/messages", { recipient_id: otherId(active), body });
      setBody("");
      const { data } = await api.get(`/conversations/${active.conversation_id}/messages`);
      setMsgs(data);
      loadConvs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-fade-in" data-testid="messages-page">
      <div className="text-xs uppercase tracking-widest text-accent mb-2">Messages</div>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mb-6">Inbox</h1>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 h-[70vh]">
        <aside className="md:col-span-4 lg:col-span-3 border border-border rounded-md bg-card overflow-y-auto">
          {loading ? (
            <div className="p-6 flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
          ) : convs.length === 0 ? (
            <div className="p-6 text-sm text-muted-foreground">No messages yet. Contact a seller from a listing to start a conversation.</div>
          ) : convs.map((c) => {
            const oid = otherId(c);
            return (
              <button
                key={c.conversation_id}
                onClick={() => setActive(c)}
                className={`w-full text-left p-4 border-b border-border last:border-b-0 flex items-start gap-3 transition-colors duration-150 ${
                  active?.conversation_id === c.conversation_id ? "bg-muted/60" : "hover:bg-muted/30"
                }`}
                data-testid={`conv-${c.conversation_id}`}
              >
                <Avatar className="w-10 h-10">
                  <AvatarImage src={c.user_pictures?.[oid]} />
                  <AvatarFallback>{c.user_names?.[oid]?.[0] || "?"}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex justify-between items-baseline gap-2">
                    <div className="font-semibold text-sm truncate flex items-center gap-1">
                      {c.is_firearms && <Shield className="w-3 h-3 text-secondary shrink-0" />}
                      {c.user_names?.[oid] || "User"}
                    </div>
                    <div className="text-[10px] text-muted-foreground shrink-0 ml-2">{formatDistanceToNow(new Date(c.updated_at), { addSuffix: false })}</div>
                  </div>
                  <div className="text-xs text-muted-foreground truncate mt-0.5">{c.last_message}</div>
                </div>
              </button>
            );
          })}
        </aside>

        <section className="md:col-span-8 lg:col-span-9 border border-border rounded-md bg-card flex flex-col">
          {active ? (
            <>
              <div className="p-4 border-b border-border flex items-center gap-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={active.user_pictures?.[otherId(active)]} />
                  <AvatarFallback>{active.user_names?.[otherId(active)]?.[0]}</AvatarFallback>
                </Avatar>
                <div className="font-semibold">{active.user_names?.[otherId(active)]}</div>
                {activeIsFirearms && (
                  <Badge className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 ml-auto" data-testid="badge-firearms-conv">
                    <Shield className="w-3 h-3" /> Firearms conversation
                  </Badge>
                )}
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="message-thread">
                {msgs.map((m) => (
                  <div key={m.message_id} className={`flex ${m.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] px-4 py-2 rounded-md ${m.sender_id === user?.user_id ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"}`}>
                      <div className="text-sm whitespace-pre-wrap">{m.body}</div>
                      <div className={`text-[10px] mt-1 ${m.sender_id === user?.user_id ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                        {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="p-3 border-t border-border">
                {gated ? (
                  <div className="flex items-center gap-3 p-3 bg-secondary/5 border border-secondary/40 rounded-md" data-testid="messages-gate">
                    <Shield className="w-5 h-5 text-secondary shrink-0" />
                    <div className="flex-1 text-sm">
                      <div className="font-semibold">{needsPass ? "Firearms Pass required" : "Age verification required"}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {needsPass
                          ? "This conversation is about a firearm listing. You need an active Firearms Pass to send messages."
                          : "Complete the one-time age & liability check to continue this conversation."}
                      </div>
                    </div>
                    <Button
                      className="bg-secondary hover:bg-secondary/90"
                      onClick={() => navigate(needsPass ? "/subscribe" : "/firearms/verify?next=/messages")}
                      data-testid="btn-messages-gate"
                    >
                      {needsPass ? "Get Pass" : "Verify age"}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-end gap-2">
                    <Textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Type a message…" className="min-h-[44px] resize-none" data-testid="input-message" />
                    <Button onClick={send} disabled={sending || !body.trim()} className="bg-primary hover:bg-primary/90" data-testid="btn-send">
                      {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">Select a conversation</div>
          )}
        </section>
      </div>
    </div>
  );
}
